/**
 * Shapes and wording for the video-visit invite.
 *
 * The API reports why a channel could not be used as a short code
 * (`no_mobile`, `email_opted_out`, …) and this module turns each one into a
 * sentence staff can act on. The split is deliberate: codes are stable enough
 * to assert on in tests, and the wording lives on this side where changing it
 * does not mean touching a send path.
 *
 * Every string here is read by front-desk staff and clinicians, so it names
 * what is wrong and what to do about it, and never mentions the machinery that
 * produced it.
 */

export type VideoInviteChannel = 'sms' | 'email';

export const VIDEO_INVITE_CHANNELS: VideoInviteChannel[] = ['sms', 'email'];

/** What the confirmation step is told about one channel before the call starts. */
export interface VideoInviteChannelState {
  available: boolean;
  reason: string | null;
  /** The number or address the invite would go to, shown so staff can check it. */
  destination: string;
}

export type VideoInviteOptions = Record<VideoInviteChannel, VideoInviteChannelState>;

/** What the API reports about one channel after an attempt. */
export interface VideoInviteChannelResult {
  sent: boolean;
  reason: string | null;
}

export type VideoInviteResults = Partial<Record<VideoInviteChannel, VideoInviteChannelResult>>;

export interface VideoInviteResponse {
  /** True when at least one channel got through. */
  sent: boolean;
  joinUrl: string;
  results: VideoInviteResults;
}

export const VIDEO_INVITE_CHANNEL_LABEL: Record<VideoInviteChannel, string> = {
  sms: 'Text',
  email: 'Email',
};

/** "Text invite sent" / "Email invite sent" — the confirmation chip. */
export function videoInviteSentText(channel: VideoInviteChannel): string {
  return `${VIDEO_INVITE_CHANNEL_LABEL[channel]} invite sent`;
}

/**
 * Why a channel is unavailable, or why a send did not land.
 *
 * An unrecognized code still produces something useful rather than a blank or a
 * raw code: staff can always fall back to copying the link.
 */
export function videoInviteReasonText(
  channel: VideoInviteChannel,
  reason: string | null | undefined,
): string {
  switch (reason) {
    case 'no_mobile':
      return "No mobile number on file. Add one to this patient's record to send a text.";
    case 'no_email':
      return "No email address on file. Add one to this patient's record to send an email.";
    case 'invalid_email':
      return "This patient's email address doesn't look right. Correct it in their record to send an email.";
    case 'email_opted_out':
      return 'This patient has asked not to receive emails. Send them a text instead.';
    case 'no_sms_number':
      return "Texting isn't set up for this practice yet. Ask your administrator to set up a texting number.";
    default:
      return channel === 'sms'
        ? "The text couldn't be sent. Try again, or copy the link below and send it yourself."
        : "The email couldn't be sent. Try again, or copy the link below and send it yourself.";
  }
}

/**
 * Which channels a freshly opened confirmation dialog should have ticked.
 *
 * Text only, and only when it can actually be used. Video calls were text-only
 * before email existed, so a staff member who opens the dialog and presses
 * Start call without reading it gets exactly what they got before.
 *
 * When we could not check what this patient has on file, text is still the
 * default: an unchecked patient must not end up harder to reach than before.
 */
export function defaultVideoInviteChannels(
  options: VideoInviteOptions | null,
): VideoInviteChannel[] {
  if (!options) return ['sms'];
  return options.sms?.available ? ['sms'] : [];
}
